/**
 * Navigation Menu Component
 *
 * @module shared/components/navigation/NavigationMenu
 *
 * Navigation menu organised into domain clusters, each with a section
 * heading when expanded. Every item is directly accessible.
 */

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListSubheader from '@mui/material/ListSubheader';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, memo } from 'react';

import { useAuthStore } from '@/modules/auth/application/store';
import { canManageOAuthClients } from '@/modules/auth/domain/access/roleAccess';
import { APP_ROUTES } from '@/shared/constants';
import { NAV_GROUPS } from './sidebarSettings';
import { NavItemButton } from './NavItemButton';

interface NavigationMenuProps {
  collapsed: boolean;
}

export const NavigationMenu = memo(function NavigationMenu({ collapsed }: NavigationMenuProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore(state => state.user);

  const isActive = useCallback(
    (path: string) => {
      // Dashboard/Home: match both "/" and "/dashboard" (due to redirect)
      if (path === '/' || path === APP_ROUTES.HOME) {
        return location.pathname === '/' || location.pathname === APP_ROUTES.HOME || location.pathname === APP_ROUTES.DASHBOARD;
      }
      // For other paths, use startsWith for nested routes
      return location.pathname.startsWith(path);
    },
    [location.pathname]
  );

  const handleItemClick = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate]
  );

  return (
    <List component="nav" sx={{ px: 0.75, py: 1, flexShrink: 0 }}>
      {NAV_GROUPS.map((group, index) => {
        const visibleItems = group.items.filter(item => !item.requiresOAuthClientManagement || canManageOAuthClients(user));

        return (
          <Box key={group.label} component="div">
            {index > 0 && <Divider sx={{ my: 0.75, mx: 0.5 }} />}
            <Box role="group" aria-label={group.label}>
              {!collapsed && (
                <ListSubheader
                  component="div"
                  disableSticky
                  sx={{
                    px: 1.25,
                    py: 0.5,
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                    lineHeight: 'normal',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    bgcolor: 'transparent',
                  }}
                >
                  {group.label}
                </ListSubheader>
              )}
              {visibleItems.map(item => (
                <NavItemButton key={item.path} item={item} collapsed={collapsed} isActive={isActive(item.path)} onClick={() => handleItemClick(item.path)} />
              ))}
            </Box>
          </Box>
        );
      })}
    </List>
  );
});
